import * as React from 'react';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import { styled, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';

function valuetext(value: number, units?: string) {
    return `${value}${units}`;
}

interface InputSliderRangeProps {
    name: string;
    marks?: { value: number; label: string }[];
    min?: number;
    max?: number;
    units?: string;
    defaultMin?: number;
    defaultMax?: number;
    minLabel?: string;
    maxLabel?: string;
    minDistance?: number;
    onChange?: (value: { min: number; max: number }) => void;
}

const SliderRangeStatic = styled(Slider)(({ theme }) => ({
    marginTop: '20px',

    // Reset default label styles to avoid conflicts
    '& .MuiSlider-valueLabelOpen': {
        borderRadius: '12px',
        background: theme.palette.primaryPurple.main,
        fontSize: '12px',
        color: theme.palette.neutralWhite.main,
        fontWeight: 600,
        lineHeight: 'normal',
        position: 'absolute',
        transform: 'none !important', // Reset default transforms
    },

    // Reset default arrow to avoid conflicts
    '& .MuiSlider-valueLabel::before': {
        display: 'none !important', // Hide default arrow, will be shown specifically
    },

    // Left slider (data-index="0") specific positioning
    '& .MuiSlider-thumb[data-index="0"] .MuiSlider-valueLabelOpen': {
        borderRadius: '12px',
        background: theme.palette.primaryPurple.main,
        fontSize: '12px',
        color: theme.palette.neutralWhite.main,
        fontWeight: 600,
        lineHeight: 'normal',
        position: 'absolute !important',
        top: '-30px !important',
        left: '1px !important',
        right: 'auto !important',
        transform: 'none !important',
    },

    '& .MuiSlider-thumb[data-index="0"] .MuiSlider-valueLabel::before': {
        display: 'block !important',
        position: 'absolute !important',
        content: '""',
        width: '8px',
        height: '8px',
        WebkitTransform: 'translate(-50%, 50%) rotate(45deg) !important',
        MozTransform: 'translate(-50%, 50%) rotate(45deg) !important',
        msTransform: 'translate(-50%, 50%) rotate(45deg) !important',
        transform: 'translate(-50%, 50%) rotate(45deg) !important',
        backgroundColor: 'inherit',
        bottom: '2px !important',
        left: '10px !important',
        right: 'auto !important',
    },

    // Right slider (data-index="1") specific positioning
    '& .MuiSlider-thumb[data-index="1"] .MuiSlider-valueLabelOpen': {
        borderRadius: '12px',
        background: theme.palette.primaryPurple.main,
        fontSize: '12px',
        color: theme.palette.neutralWhite.main,
        fontWeight: 600,
        lineHeight: 'normal',
        position: 'absolute !important',
        top: '-30px !important',
        right: '1px !important',
        left: 'auto !important',
        transform: 'none !important',
    },

    '& .MuiSlider-thumb[data-index="1"] .MuiSlider-valueLabel::before': {
        display: 'block !important',
        position: 'absolute !important',
        content: '""',
        width: '8px',
        height: '8px',
        WebkitTransform: 'translate(-50%, 50%) rotate(45deg) !important',
        MozTransform: 'translate(-50%, 50%) rotate(45deg) !important',
        msTransform: 'translate(-50%, 50%) rotate(45deg) !important',
        transform: 'translate(-50%, 50%) rotate(45deg) !important',
        backgroundColor: 'inherit',
        bottom: '2px !important',
        right: '3px !important',
        left: 'auto !important',
    },

    '& .MuiSlider-rail': {
        background: '#F7F6FF',
        height: '6px',
    },
}));

export default function InputSliderRangeStatic({
    name,
    marks,
    min,
    max,
    defaultMin,
    defaultMax,
    units,
    minLabel,
    maxLabel,
    minDistance,
    onChange,
}: InputSliderRangeProps) {
    const { setValue, watch } = useFormContext();
    const formValue = watch(name);

    const [value, setLocalValue] = React.useState<Record<string, any>>({
        min: defaultMin ?? 0,
        max: defaultMax ?? 100,
    });

    // Update local state when form value changes
    React.useEffect(() => {
        if (formValue) {
            if (Array.isArray(formValue)) {
                setLocalValue({
                    min: formValue[0],
                    max: formValue[1],
                });
            } else if (typeof formValue === 'object' && formValue.min !== undefined && formValue.max !== undefined) {
                setLocalValue({
                    min: formValue.min ?? defaultMin ?? 0,
                    max: formValue.max ?? defaultMax ?? 100,
                });
            } else {
                setLocalValue({
                    min: defaultMin ?? 0,
                    max: defaultMax ?? 100,
                });
            }
        }
    }, [defaultMax, defaultMin, formValue]);

    const handleChange1 = (event: Event, sliderValues: number[], activeThumb: number) => {
        let updatedValue: number[];

        if (activeThumb === 0) {
            updatedValue = [Math.min(sliderValues[0], value.max - (minDistance ?? 0)), value.max];
        } else {
            updatedValue = [value.min, Math.max(sliderValues[1], value.min + (minDistance ?? 0))];
        }

        const _newValue = { min: updatedValue[0], max: updatedValue[1] };
        setLocalValue(_newValue);
        setValue(name, _newValue);
        onChange?.(_newValue);
    };

    return (
        <Box sx={{ padding: '10px' }}>
            <SliderRangeStatic
                getAriaLabel={() => 'Minimum distance'}
                value={[value.min, value.max]}
                onChange={handleChange1}
                valueLabelFormat={val => valuetext(val, units)}
                disableSwap
                marks={marks}
                valueLabelDisplay="on"
                min={min}
                max={max}
            />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: theme => theme.palette.neutralDarkGray.main,
                    fontFamily: theme => theme.typography.fontFamily,
                }}>
                <Typography variant="body2" sx={{ cursor: 'pointer' }}>
                    {minLabel}
                </Typography>
                <Typography variant="body2" sx={{ cursor: 'pointer' }}>
                    {maxLabel}
                </Typography>
            </Box>
        </Box>
    );
}
